import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PlusIcon from '~/assets/icons/plus.svg';
import FolderIcon from '~/assets/icons/folder.svg';
import LightButton from '~/editor/buttons/LightButton';
import ArrowOrderIcon from '~/assets/icons/arrow-order.svg';
import PopupMenuCreate from '~/editor/popupMenu/PopupMenuCreate';
import { useEditorStore, EditorChapter } from '~/shell/Store';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import { Button } from '~/ui/buttons';
import ModalCloseButton from '~/ui/buttons/ModalCloseButton';
import NoSearchResultFoundIcon from '~/assets/icons/no_search_result_found.svg';
import PurpleSearchIcon from '~/assets/icons/purple_search.svg';
import { x } from '@xstyled/styled-components';

const PlusIconForBtn = styled(PlusIcon)`
  float: left;
  margin-left: 20px;
`;

const FolderIconForBtn = styled(FolderIcon)``;

const CreateNewSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: center;

  button {
    width: 224px;
    font-family: ralewayBold;
    font-size: 14px;
    font-weight: unset !important;

    svg {
      margin-top: -2px;
      margin-left: 36px;
      margin-right: -26px;
    }
  }
`;

const TemplateTypeWrapper = styled.div`
  padding: 40px;
  height: 100%;

  .closeBtn {
    right: 40px;
    left: auto;
  }

  input[type='text'] {
    width: 100%;
    height: 40px;
    border-radius: 8px;
    background-color: #f5f5f5;
    color: #6f6f6f;
    padding: 10px;

    font-family: Raleway;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
  }

  .footer {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    gap: 10px;
    margin-top: auto;
  }
`;

const TemplateTypeSectionWrapper = styled.div`
  flex-grow: 1;
  height: calc(100% - 140px);
  min-height: 200px;
  margin-bottom: 20px;
  margin-top: 20px;
  scrollbar-gutter: stable;
`;

const TemplateTypeTitle = styled.div`
  font-family: Recoleta;
  font-size: 24px;
  font-weight: 400;
  line-height: 32.64px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  margin-bottom: 10px;
`;

const TemplateType = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;

  .template-list-header {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;

    .title {
      display: flex;
      flex-direction: column;

      b {
        font-family: ralewayBold;
        font-size: 16px;
        font-weight: unset !important;
        line-height: 24px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
      }

      span {
        font-family: Raleway;
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #a9a9a9;
      }
    }

    svg.arrow {
      display: block;
      margin-left: auto;
      background-color: transparent;
      margin-right: 10px;
      transition: transform 0.3s ease;
      path {
        fill: #9738ff;
      }
    }

    svg.arrow.open {
      transform: rotate(180deg);
      transition: transform 0.3s ease;
    }
  }

  .template-list-items {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;

    .template-item {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-direction: column;
      cursor: pointer;

      img {
        width: 263px;
        height: 151px;
        border-radius: 4px;
        border: 1px solid #a9a9a9;
      }

      span {
        font-family: Raleway;
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        text-align: center;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #2e2e2e;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 263px;
      }
    }
    .template-item.selected {
      img {
        border: 2px solid #9945fb;
      }
    }
  }
`;

const TemplateList = ({
  title,
  items,
  setSelectedTemplate,
  selectedTemplate,
  openBlock,
  searchQuery
}: {
  title: string;
  items: Record<string, unknown>[];
  setSelectedTemplate: (template: Record<string, unknown>) => void;
  selectedTemplate: Record<string, unknown> | null;
  openBlock?: boolean | undefined;
  searchQuery: string;
}) => {
  const [open, setOpen] = useState(openBlock);
  const firstItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchQuery) return;

    if (open && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    setOpen(openBlock);
  }, [openBlock]);

  return (
    <TemplateType>
      <div className="template-list-header" onClick={() => setOpen(!open)}>
        <FolderIconForBtn />
        <div className="title">
          <b>{title}</b>
          <span>{items.length} Templates</span>
        </div>
        <ArrowOrderIcon className={open ? 'arrow open' : 'arrow'} />
      </div>
      {open && (
        <div className="template-list-items">
          {items.map(
            (
              item: {
                id: string;
                friendlyName: string;
                name: string;
                screenshotUrl: string;
              },
              index: number
            ) => (
              <div
                key={item.id}
                ref={index === 0 ? firstItemRef : null}
                className={`template-item ${
                  selectedTemplate?.id === item.id ? 'selected' : ''
                }`}
                onClick={() => setSelectedTemplate(item)}
                tabIndex={0}
              >
                <img src={item.screenshotUrl} alt={item.friendlyName} />
                <span>{item.friendlyName}</span>
              </div>
            )
          )}
        </div>
      )}
    </TemplateType>
  );
};

const Title = styled.div`
  font-family: ralewayBold;
  font-size: 14px;
  font-weight: unset !important;
  line-height: 16.44px;
  letter-spacing: 0.1em;
  text-align: left;
  color: rgb(0, 0, 0);
  padding-left: 10px;
  padding-bottom: 5px;
  padding-top: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 240px;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
`;

const TitleInput = styled.input`
  font-family: ralewayBold;
  font-size: 14px;
  font-weight: unset !important;
  line-height: 16.44px;
  letter-spacing: 0.1em;
  text-align: left;
  color: rgb(0, 0, 0);
  padding: 5px 10px;
  border: 2px solid #007bff;
  border-radius: 4px;
  background-color: white;
  width: 240px;
  outline: none;

  &:focus {
    border-color: #0056b3;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const SearchInput = styled.input`
  padding-left: 35px !important;
  padding-right: 0px !important;
  border: 2px solid transparent;
  outline: none;

  &:focus {
    border: 2px solid #9738ff;
  }
`;

const searchTemplates = (
  query: string,
  templates: { friendlyName: string; name: string }[] | undefined
) => {
  const normalizedQuery = query.toLowerCase();
  const normalizedTemplates = templates || [];

  if (!normalizedQuery) return normalizedTemplates;

  const foundTemplates = normalizedTemplates.filter(
    (template: { friendlyName: string; name: string }) => {
      if (template.friendlyName.toLowerCase().includes(normalizedQuery)) {
        return true;
      }
      if (template.name.toLowerCase().includes(normalizedQuery)) return true;

      return false;
    }
  );

  return foundTemplates;
};

const CancelButton = ({ onClick }: { onClick: () => void }) => (
  <LightButton
    fontFamily="ralewayBold"
    fontSize="14px !important"
    onClick={onClick}
  >
    Cancel
  </LightButton>
);

const AddNewButton = ({
  disabled,
  onClick
}: {
  disabled: boolean;
  onClick: () => void;
}) => (
  <Button
    fontFamily="ralewayBold"
    fontSize="14px !important"
    disabled={disabled}
    onClick={onClick}
  >
    Add New
  </Button>
);

export const CreateNew = ({
  courseTitle,
  onCreateNewChapter
}: {
  courseTitle: string;
  onCreateNewChapter: () => void;
}) => {
  const firstTemplateRef = useRef<HTMLDivElement | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(courseTitle);

  const [textForCreating, setTextForCreating] = useState('Creating');
  const [creating, setCreating] = useState(false);
  const [openCreateNewSlide, setOpenCreateNewSlide] = useState(false);
  const [openCreateNewChapter, setOpenCreateNewChapter] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const {
    training,
    setTraining,
    setSequence,
    apiService,
    sequence,
    updateTrainingTitle,
    templates,
    templatesLoading,
    templatesError
  } = useEditorStore();

  useEffect(() => {
    setEditedTitle(courseTitle);
  }, [courseTitle]);

  const handleTitleDoubleClick = () => {
    setIsEditingTitle(true);
    setEditedTitle(editedTitle);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleSave = () => {
    if (editedTitle.trim()) {
      updateTrainingTitle(editedTitle.trim());
      setIsEditingTitle(false);
    }
  };

  const handleTitleCancel = () => {
    setEditedTitle(courseTitle);
    setIsEditingTitle(false);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  useEffect(() => {
    if (openCreateNewSlide && firstTemplateRef.current) {
      firstTemplateRef.current.focus();
    }
  }, [openCreateNewSlide]);

  const handleCreate = () => {
    if (selectedTemplate && training) {
      setCreating(true);

      const currentChapter = training.chapters.find((chapter: EditorChapter) =>
        chapter.chapterSequences.some(
          (chapterSequence: EditorChapter['chapterSequences'][number]) =>
            chapterSequence.sequence.id === sequence!.id
        )
      );

      const currentSequence = currentChapter!.chapterSequences.find(
        (chapterSequence: EditorChapter['chapterSequences'][number]) =>
          chapterSequence.sequence.id === sequence!.id
      );

      apiService
        ?.createSequenceByTemplate(
          training.id as number,
          selectedTemplate.id as string,
          currentChapter!.id as number,
          currentSequence!.position! + 1,
          selectedTemplate.friendlyName as string
        )
        .then(
          (data: {
            chapterSequence?: EditorChapter['chapterSequences'][number];
          }) => {
            if (data && data.chapterSequence && data.chapterSequence.sequence) {
              setSequence(data.chapterSequence.sequence);
            }

            const newTraining = { ...training };

            const chapterIndex = newTraining.chapters.findIndex(
              (chapter: EditorChapter) =>
                chapter.chapterSequences.some(
                  (
                    chapterSequence: EditorChapter['chapterSequences'][number]
                  ) => chapterSequence.sequence.id === sequence!.id
                )
            );

            const sequenceIndex = newTraining.chapters[
              chapterIndex
            ].chapterSequences.findIndex(
              (chapterSequence: EditorChapter['chapterSequences'][number]) =>
                chapterSequence.sequence.id === sequence!.id
            );

            newTraining.chapters[chapterIndex].chapterSequences.splice(
              sequenceIndex + 1,
              0,
              { ...data.chapterSequence! }
            );

            setTraining(newTraining);
            setCreating(false);
            setOpenCreateNewSlide(false);
          }
        );
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (creating) {
      interval = setInterval(() => {
        setTextForCreating(prev => {
          if (prev === 'Creating...') {
            return 'Creating';
          }
          return prev + '.';
        });
      }, 250);
    } else {
      setTextForCreating('Creating');
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [creating]);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
    setOpenPopup(true);
  };

  const handleSearchQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value.length > 100) return;

    setSearchQuery(event.target.value);
    setSelectedTemplate(null);
  };

  const handleOverlayClick = () => {
    setOpenCreateNewSlide(false);
    setSearchQuery('');
    setSelectedTemplate(null);
  };

  const handleCloseButtonClick = () => {
    setOpenCreateNewSlide(false);
    setSearchQuery('');
    setSelectedTemplate(null);
  };

  const foundTextTemplates = searchTemplates(searchQuery, templates?.text);
  const foundMediaTemplates = searchTemplates(searchQuery, templates?.media);
  const foundCommunicationTemplates = searchTemplates(
    searchQuery,
    templates?.communication
  );
  const foundInteractiveTemplates = searchTemplates(
    searchQuery,
    templates?.interactive
  );
  const foundQuizTemplates = searchTemplates(searchQuery, templates?.quiz);

  const shouldShowTemplates = () => {
    if (!searchQuery) return true;
    if (foundTextTemplates.length > 0) return true;
    if (foundMediaTemplates.length > 0) return true;
    if (foundCommunicationTemplates.length > 0) return true;
    if (foundInteractiveTemplates.length > 0) return true;
    if (foundQuizTemplates.length > 0) return true;

    return false;
  };

  return (
    <>
      {!openCreateNewChapter && (
        <CreateNewSection>
          {isEditingTitle ? (
            <TitleInput
              value={editedTitle}
              onChange={handleTitleChange}
              onKeyDown={handleTitleKeyPress}
              onBlur={handleTitleSave}
              autoFocus
              placeholder="Enter course title"
            />
          ) : (
            <Title onDoubleClick={handleTitleDoubleClick}>
              {editedTitle.toUpperCase()}
            </Title>
          )}
          <LightButton
            onClick={handleButtonClick}
            disabled={templatesLoading || !!templatesError}
          >
            <PlusIconForBtn />
            Add New
          </LightButton>
        </CreateNewSection>
      )}
      <PopupMenuCreate
        isOpen={openPopup}
        anchorElement={anchorElement}
        onClose={() => setOpenPopup(false)}
        onNewSlide={() => setOpenCreateNewSlide(true)}
        onNewChapter={onCreateNewChapter}
      />
      <Overlay open={openCreateNewSlide} onClick={handleOverlayClick}>
        <Modal
          horizontal="center"
          open
          vertical="center"
          width="91%"
          height="90%"
          minHeight="360px"
          borderRadius="21px"
        >
          <TemplateTypeWrapper>
            <ModalCloseButton onClick={handleCloseButtonClick} />

            <x.div
              display="flex"
              flexDirection={{ _: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems="center"
            >
              <TemplateTypeTitle>Select A Slide Template</TemplateTypeTitle>
              <x.div w={{ _: '100%', md: '30%' }} position="relative">
                <x.div
                  position="absolute"
                  top="-37.5"
                  left="-37.5"
                  transform="scale(0.15)"
                >
                  <PurpleSearchIcon />
                </x.div>
                <SearchInput
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  aria-label="Search templates"
                />
              </x.div>
            </x.div>
            <x.div
              fontFamily="RalewayBold"
              fontWeight="700"
              minHeight={{ _: 'auto', md: '25px' }}
            >
              {searchQuery && `Search results for '${searchQuery}'`}
            </x.div>
            <TemplateTypeSectionWrapper
              style={{ overflowY: shouldShowTemplates() ? 'auto' : 'hidden' }}
            >
              {shouldShowTemplates() ? (
                <>
                  {foundTextTemplates.length > 0 && (
                    <TemplateList
                      openBlock={!!searchQuery || true}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      title="Text-Based Templates"
                      items={foundTextTemplates}
                      searchQuery={searchQuery}
                    />
                  )}
                  {foundMediaTemplates.length > 0 && (
                    <TemplateList
                      openBlock={!!searchQuery || false}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      title="Media Templates"
                      items={foundMediaTemplates}
                      searchQuery={searchQuery}
                    />
                  )}
                  {foundCommunicationTemplates.length > 0 && (
                    <TemplateList
                      openBlock={!!searchQuery || false}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      title="Communication Scenario Templates"
                      items={foundCommunicationTemplates}
                      searchQuery={searchQuery}
                    />
                  )}
                  {foundInteractiveTemplates.length > 0 && (
                    <TemplateList
                      openBlock={!!searchQuery || false}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      title="Interactive Content Templates"
                      items={foundInteractiveTemplates}
                      searchQuery={searchQuery}
                    />
                  )}
                  {foundQuizTemplates.length > 0 && (
                    <TemplateList
                      openBlock={!!searchQuery || false}
                      setSelectedTemplate={setSelectedTemplate}
                      selectedTemplate={selectedTemplate}
                      title="Knowledge Check Templates"
                      items={foundQuizTemplates}
                      searchQuery={searchQuery}
                    />
                  )}
                </>
              ) : (
                <x.div
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={{ _: 'auto', md: '100%' }}
                  gap="10px"
                >
                  <x.div scale="1" pb="30px">
                    <NoSearchResultFoundIcon />
                  </x.div>
                  <span>No search results found</span>
                </x.div>
              )}
            </TemplateTypeSectionWrapper>
            <div className="footer">
              <CancelButton onClick={handleCloseButtonClick} />

              {creating ? (
                <LightButton
                  disabled
                  style={{ display: 'flex', width: '130px' }}
                >
                  <span>{textForCreating}</span>
                </LightButton>
              ) : (
                <AddNewButton
                  disabled={!selectedTemplate}
                  onClick={handleCreate}
                />
              )}
            </div>
          </TemplateTypeWrapper>
        </Modal>
      </Overlay>
    </>
  );
};
